/**
 * Updates — Status and Channels in one tab.
 *
 * From docs/references/17-updates-top.png and 18-updates-channels.png.
 *
 * Two things in those references overturned what I had assumed:
 *
 *   Status is a horizontal rail of TALL CARDS, not a row of circles. The
 *   circles are the old design. A card can show the status itself behind the
 *   name, which is the point — you choose what to open by seeing it.
 *
 *   Channels is a full section, not a list: a header with Explore, the
 *   channels you follow, then suggestions with Follow buttons, then Explore
 *   more and Create channel.
 *
 * Both halves are live now. The suggestions are whatever the server's
 * discovery endpoint returns, ordered by follower count — not a hand-written
 * list, which is why this file contains no channel names.
 */

import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { status as statusService, channels as channelService } from '@zivapp/shared'
import { useAuth } from '../context/index.js'
import StatusRail from '../components/status/StatusRail.jsx'
import { FollowedRow, SuggestionRow } from '../components/channels/ChannelRows.jsx'
import './Updates.css'

/** How many suggestions fit before the section stops being a suggestion. */
const SUGGESTION_LIMIT = 4

export default function Updates () {
  const { user } = useAuth()
  const navigate = useNavigate()

  const [feed, setFeed] = useState([])
  const [mine, setMine] = useState([])

  const [followed, setFollowed] = useState([])
  const [suggested, setSuggested] = useState([])
  const [dismissed, setDismissed] = useState(() => new Set())
  const [following, setFollowing] = useState(null)

  useEffect(() => statusService.subscribeToFeed(setFeed), [])
  useEffect(() => statusService.subscribeToMine(setMine), [])
  useEffect(() => channelService.subscribeToMine(setFollowed), [])

  /*
   * Discovery is a one-off fetch, not a subscription.
   *
   * What is popular across the whole platform does not change while someone
   * is looking at this screen, and subscribing to it would mean every client
   * refetching the global list on every post anybody makes anywhere.
   */
  const loadSuggestions = useCallback(async () => {
    try {
      setSuggested(await channelService.search(''))
    } catch {
      // Discovery failing is not worth an error state on a tab whose other
      // half works. The section simply shows nothing to suggest.
      setSuggested([])
    }
  }, [])

  useEffect(() => { loadSuggestions() }, [loadSuggestions])

  const follow = async channel => {
    setFollowing(channel.id)
    try {
      await channelService.follow(channel.id)
      // subscribeToMine brings it into the followed list on its own; drop it
      // from suggestions here so the row does not sit in both at once.
      setSuggested(list => list.filter(c => c.id !== channel.id))
    } finally {
      setFollowing(null)
    }
  }

  // Dismissal lasts as long as the screen does. Remembering it properly
  // needs a column of its own, and guessing at one now would mean a
  // migration later to correct it.
  const dismiss = channel => setDismissed(set => new Set(set).add(channel.id))

  const visibleSuggestions = suggested
    .filter(c => !c.isFollowing && !dismissed.has(c.id))
    .slice(0, SUGGESTION_LIMIT)

  const hasChannels = followed.length > 0 || visibleSuggestions.length > 0

  return (
    <div className="up">
      <header className="up__bar">
        <h1 className="up__title">Updates</h1>
        <div className="up__bar-actions">
          <button
            className="up__icon-btn"
            type="button"
            aria-label="Search"
            onClick={() => navigate('/channels')}
          >
            <svg viewBox="0 0 24 24" fill="none">
              <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="1.9" />
              <path d="m16.5 16.5 4 4" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" />
            </svg>
          </button>
          <button className="up__icon-btn" type="button" aria-label="Menu">
            <svg viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="5" r="1.7" fill="currentColor" />
              <circle cx="12" cy="12" r="1.7" fill="currentColor" />
              <circle cx="12" cy="19" r="1.7" fill="currentColor" />
            </svg>
          </button>
        </div>
      </header>

      <div className="up__scroll">
        <section className="up__section">
          <h2 className="up__heading">Status</h2>
          <StatusRail
            mine={mine}
            feed={feed}
            me={user}
            onAdd={() => navigate('/status/new/photo')}
          />
        </section>

        <section className="up__section">
          <div className="up__heading-row">
            <h2 className="up__heading">Channels</h2>
            <button
              type="button"
              className="up__explore"
              onClick={() => navigate('/channels')}
            >
              Explore
            </button>
          </div>

          {followed.map(channel => (
            <FollowedRow key={channel.id} channel={channel} />
          ))}

          {visibleSuggestions.map(channel => (
            <SuggestionRow
              key={channel.id}
              channel={channel}
              onFollow={follow}
              onDismiss={dismiss}
              busy={following === channel.id}
            />
          ))}

          {/*
            Shown only when there is genuinely nothing — neither followed nor
            suggested. Inventing "ShopGoods · 55 unread" to fill the space
            would be the fake-contacts mistake again.
          */}
          {!hasChannels && (
            <div className="up__channels-empty">
              <span className="up__channels-art" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none">
                  <path d="M4 9.5h3.2L14 5.2v13.6L7.2 14.5H4a1.5 1.5 0 0 1-1.5-1.5v-2a1.5 1.5 0 0 1 1.5-1.5Z"
                    stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
                  <path d="M17.4 9a4.4 4.4 0 0 1 0 6M19.8 6.4a8 8 0 0 1 0 11.2"
                    stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
                </svg>
              </span>
              <p className="up__channels-title">No channels yet</p>
              <p className="up__channels-text">
                Channels are one-way broadcasts you can follow. Find one to
                follow, or start your own.
              </p>
            </div>
          )}

          <div className="up__actions">
            <button type="button" className="up__wide" onClick={() => navigate('/channels')}>
              <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <rect x="3.5" y="3.5" width="7" height="7" rx="1.6" stroke="currentColor" strokeWidth="1.7" />
                <rect x="13.5" y="3.5" width="7" height="7" rx="1.6" stroke="currentColor" strokeWidth="1.7" />
                <rect x="3.5" y="13.5" width="7" height="7" rx="1.6" stroke="currentColor" strokeWidth="1.7" />
                <rect x="13.5" y="13.5" width="7" height="7" rx="1.6" stroke="currentColor" strokeWidth="1.7" />
              </svg>
              Explore more
            </button>

            <button
              type="button"
              className="up__wide"
              data-testid="create-channel"
              onClick={() => navigate('/channels/new')}
            >
              <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
              Create channel
            </button>
          </div>
        </section>
      </div>

      {/* Two actions, as in the reference: a written status and a photo one.
          The camera is the primary of the pair, so it is the filled one. */}
      <div className="up__fabs">
        <button
          type="button"
          className="up__fab up__fab--minor"
          aria-label="Write a status"
          data-testid="write-status"
          onClick={() => navigate('/status/new/text')}
        >
          <svg viewBox="0 0 24 24" fill="none">
            <path d="M4 20h4L19.4 8.6a2.4 2.4 0 0 0-3.4-3.4L4.6 16.6 4 20Z"
              stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
          </svg>
        </button>

        <button
          type="button"
          className="up__fab up__fab--major"
          aria-label="Add a photo status"
          data-testid="photo-status"
          onClick={() => navigate('/status/new/photo')}
        >
          <svg viewBox="0 0 24 24" fill="none">
            <path d="M4 8h2.8l1.4-2.2h7.6L17.2 8H20a1.8 1.8 0 0 1 1.8 1.8v8.4A1.8 1.8 0 0 1 20 20H4a1.8 1.8 0 0 1-1.8-1.8V9.8A1.8 1.8 0 0 1 4 8Z"
              stroke="currentColor" strokeWidth="1.9" strokeLinejoin="round" />
            <circle cx="12" cy="13.8" r="3.4" stroke="currentColor" strokeWidth="1.9" />
          </svg>
        </button>
      </div>
    </div>
  )
}
